import Layout from "../../components/Layout/Layout";
import { Link } from "react-router-dom";
import {
  OfficeBuildingIcon,
  UserGroupIcon,
  KeyIcon,
  ArrowRightIcon,
} from "@heroicons/react/outline";
import { useState, useEffect } from "react";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";

const statsCards = [
  {
    name: "Organizations",
    href: "/thirdparty/organizations",
    icon: OfficeBuildingIcon,
    bgColor: "bg-blue-500",
    description: "Manage third-party organizations",
  },
  {
    name: "Applications",
    href: "/thirdparty/applications",
    icon: KeyIcon,
    bgColor: "bg-purple-500",
    description: "Manage API applications",
  },
  {
    name: "Employees",
    href: "/thirdparty/employees",
    icon: UserGroupIcon,
    bgColor: "bg-green-500",
    description: "Manage organization employees",
  },
];

const quickActions = [
  {
    name: "Create Organization",
    description: "Add a new third-party organization",
    href: "/thirdparty/organizations/create",
    icon: OfficeBuildingIcon,
  },
  {
    name: "Create Application",
    description: "Create new API application",
    href: "/thirdparty/applications?action=create",
    icon: KeyIcon,
  },
];

export default function ThirdPartyDashboard() {
  const [stats, setStats] = useState({
    organizations: 0,
    applications: 0,
    employees: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentOrganizations, setRecentOrganizations] = useState([]);
  const [recentEmployees, setRecentEmployees] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch organizations first
      const orgResponse = await ThirdPartyServices.listOrganizations(1, 5);
      if (orgResponse.status && orgResponse.data?.result) {
        setRecentOrganizations(orgResponse.data.result.slice(0, 5));
        setStats((prev) => ({
          ...prev,
          organizations: orgResponse.data.totalCount || 0,
        }));

        // Set the first organization as selected if available
        if (orgResponse.data.result.length > 0) {
          setSelectedOrgId(orgResponse.data.result[0].organizationId);
        }
      }

      // Fetch employees only if we have an organization
      if (selectedOrgId) {
        const empResponse = await ThirdPartyServices.listEmployees(
          1,
          5,
          selectedOrgId,
        );
        if (empResponse.status && empResponse.data?.result) {
          setRecentEmployees(empResponse.data.result.slice(0, 5));
          setStats((prev) => ({
            ...prev,
            employees: empResponse.data.totalCount || 0,
          }));
        } else {
          setRecentEmployees([]);
          setStats((prev) => ({ ...prev, employees: 0 }));
        }
      } else {
        // If no organization selected, show empty state
        setRecentEmployees([]);
        setStats((prev) => ({ ...prev, employees: 0 }));
      }

      // Fetch applications count
      const appResponse = await ThirdPartyServices.listApplications(1, 1);
      if (appResponse.status && appResponse.data?.result) {
        setStats((prev) => ({
          ...prev,
          applications: appResponse.data.totalCount || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle organization change
  const handleOrganizationChange = async (orgId) => {
    setSelectedOrgId(orgId);
    setLoading(true);

    try {
      if (orgId) {
        const empResponse = await ThirdPartyServices.listEmployees(1, 5, orgId);
        if (empResponse.status && empResponse.data?.result) {
          setRecentEmployees(empResponse.data.result.slice(0, 5));
          setStats((prev) => ({
            ...prev,
            employees: empResponse.data.totalCount || 0,
          }));
        } else {
          setRecentEmployees([]);
          setStats((prev) => ({ ...prev, employees: 0 }));
        }
      } else {
        setRecentEmployees([]);
        setStats((prev) => ({ ...prev, employees: 0 }));
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setRecentEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout
        title="Third-Party Dashboard"
        current="thirdparty"
        pageTag="thirdparty"
      >
        <div className="flex justify-center items-center h-64">
          <HollowDotsSpinner color="#4A90E2" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Third-Party Dashboard"
      current="thirdparty"
      pageTag="thirdparty"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <p className="mt-2 text-sm text-gray-600">
            Manage third-party integrations, organizations, and employees
          </p>
        </div>

        {/* Organization Selector for Dashboard */}
        <div className="mb-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Select Organization
          </h2>
          <div className="flex items-center space-x-4">
            <select
              value={selectedOrgId || ""}
              onChange={(e) => handleOrganizationChange(e.target.value)}
              className="block w-full md:w-1/3 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">Select an organization</option>
              {recentOrganizations.map((org) => (
                <option key={org.organizationId} value={org.organizationId}>
                  {org.companyName} ({org.applicationName})
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500">
              {selectedOrgId
                ? `Viewing data for selected organization`
                : "Please select an organization to view employees"}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          {statsCards.map((card) => {
            const Icon = card.icon;
            return (
              <Link
                key={card.name}
                to={card.href}
                className="relative bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {card.name}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                      {stats[card.name.toLowerCase()] || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {card.description}
                    </p>
                  </div>
                  <div className={`${card.bgColor} p-3 rounded-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm text-blue-600">
                  <span>View all</span>
                  <ArrowRightIcon className="h-4 w-4 ml-1" />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Quick Actions
            </h2>
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg divide-y divide-gray-200">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={action.name}
                    to={action.href}
                    className="flex items-center p-4 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <div className="flex-shrink-0">
                      <Icon className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {action.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {action.description}
                      </p>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Recent Organizations */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Recent Organizations
            </h2>
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
              {recentOrganizations.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {recentOrganizations.map((org) => (
                    <div key={org.organizationId} className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {org.companyName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {org.applicationName}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            org.isOnboarded
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {org.isOnboarded ? "Onboarded" : "Pending"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <OfficeBuildingIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No organizations yet</p>
                  <Link
                    to="/thirdparty/organizations/create"
                    className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
                  >
                    Create your first organization
                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              )}
              <div className="border-t border-gray-200 p-4">
                <Link
                  to="/thirdparty/organizations"
                  className="text-sm font-medium text-blue-600 hover:text-blue-500 flex items-center justify-center"
                >
                  View all organizations
                  <ArrowRightIcon className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Employees - Only show if organization is selected */}
        {selectedOrgId && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Recent Employees
              </h2>
              <Link
                to={`/thirdparty/employees?organization=${selectedOrgId}`}
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                View all
              </Link>
            </div>
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
              {recentEmployees.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Organization
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentEmployees.map((employee) => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {employee.firstName} {employee.surname}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {employee.userName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {employee.emailAddress}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {selectedOrgId.substring(0, 8)}...
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    No employees found for this organization
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activity Summary */}
        <div className="mt-8 bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Activity Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {stats.organizations}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Total Organizations
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {stats.applications}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Active Applications
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {stats.employees}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Registered Employees
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
